import {
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import * as IAction from 'app/interfaces/actiontracker.interface';
import { ActionTrackerService } from 'app/services/actiontracker.service';
import { NotificationsService } from 'app/services/notifications.service';
import { CRMSteps } from 'modules/promptuary/constants/crm-steps';
import { IIcd } from 'modules/promptuary/interfaces/icd.interface';
import { IAppointment } from 'shared/interfaces/appointment.interface';
import { IPromptuary } from 'shared/interfaces/promptuary.interface';
import { AppointmentService } from 'shared/services/appointment/appointment.service';
import { ContactService } from 'shared/services/contact/contact.service';
import { PromptuaryService } from 'shared/services/promptuary/promptuary.service';

import { UnifiedPromptuaryService } from '../unified-promptuary.service';

@Component({
	selector: 'promptuary-form-crm',
	templateUrl: './promptuary-form-crm.component.html',
	styleUrls: ['./promptuary-form-crm.component.scss'],
})
export class PromptuaryFormCRM implements OnInit, OnDestroy {
	alarmSignals: boolean;
	appointment: IAppointment;
	autoSaveData;
	contact = null;
	dataError = false;
	firstName: string;
	formSteps = CRMSteps;
	icdOptions: IIcd[] = [];
	memedElemScriptId = 'memed-integration';
	tabIndex = 0;
	visible = true;
	tabList = [
		{ name: 'PROMPTUARY.PROMPTUARY' },
		{ name: 'PROMPTUARY.PRESCRIPTION' },
	];
	_icd = [];
	stepsComplete = false;
	@Input() cachedICD: IIcd[] = [];
	@Input() promptuary: IPromptuary;
	@Input() from;
	@Input() mainType;
	@Input() appointmentId: string;
	@Input() clientId: string;
	@Input() type: 'page' | 'sidebar' = 'page';
	@Input() isAssistanceJourney = false;
	@Output('pageLoaded') emPageLoaded = new EventEmitter<boolean>();
	@Output('savedPromptuary') emSavedPromptuary = new EventEmitter<boolean>();
	@Output() cancel = new EventEmitter<any>();

	constructor(
		public actionTrackerService: ActionTrackerService,
		public appointmentService: AppointmentService,
		public notificationService: NotificationsService,
		public contactService: ContactService,
		public promptuaryService: PromptuaryService,
		public router: Router,
		public translateService: TranslateService,
		public unifiedPromptuary: UnifiedPromptuaryService
	) {}

	ngOnInit(): void {
		this._icd = this.cachedICD;
		this.appointmentService.getData(this.appointmentId).subscribe((result) => {
			this.appointment = result;
			this.unifiedPromptuary.appointmentId = this.appointmentId;
			this.autoSaveData = {
				id: this.appointmentId,
				contact: {
					id: this.appointment.profileId,
				},
			};

			this.unifiedPromptuary.isPromptuaryCreated = true;
			this.unifiedPromptuary.setFormModel(
				this.promptuary,
				this.appointment,
				'crm'
			);

			this.firstName = this.appointment.userName.split(' ')[0];
			if (typeof this.promptuary?.alarmSignals === 'boolean') {
				this.alarmSignals = this.promptuary.alarmSignals;
				this.setPromptuaryValue('alarmSignals', this.alarmSignals);
			}
			if (this.promptuary?.icd10?.length > 0) {
				this.updateStep({ property: 'icd10', newValue: true });
			}
			this.contactService
				.getData(this.appointment.contactId)
				.toPromise()
				.then((contact) => {
					this.contact = contact;
				})
				.catch((err) => {
					console.log('contact', err);
				});
		});

		this.actionTrackerService.eventTracker(
			IAction.SCOPE_TYPE.Promptuary,
			IAction.SUBSCOPE_TYPE.Session,
			this.type === 'page' ? IAction.ITEM_TYPE.Page : IAction.ITEM_TYPE.Sidebar,
			IAction.INTERACTION_TYPE.View,
			{
				specialist_type: this.mainType,
				from: this.from,
			}
		);

		this.emPageLoaded.emit(true);
	}

	ngOnDestroy(): void {
		const memedScript = document.getElementById(this.memedElemScriptId || '');

		if (memedScript) {
			memedScript.remove();
		}
	}

	goBack() {
		if (history.state?.returnTo) {
			this.router.navigate([history.state.returnTo]);
			return;
		}

		this.appointment.contactId
			? this.router.navigate([`/contact/${this.appointment.contactId}`])
			: this.router.navigate(['/clients']);
	}

	getStepByProperty(property: string): string {
		const stepMap = {
			alarmSignalsReason: 'alarmSignals',
			icd10: 'ICD',
		};
		return stepMap[property] ?? property;
	}

	updateStep(stepData: { newValue: string | boolean; property: string }) {
		const step = this.getStepByProperty(stepData.property);
		this.formSteps[step].complete = stepData.newValue ? true : false;

		if (stepData.property === 'session-note') {
			this.formSteps['session-note'].complete = true;
		}

		this.stepsComplete = Object.values(this.formSteps).every((elem) => {
			return elem.complete === true;
		});
	}

	setLoading(property) {
		const ignoreAutosave = ['reasons', 'icd10'];
		if (!ignoreAutosave.includes(property)) {
			this.unifiedPromptuary.autosaveLoading.push(property);
		}
	}

	changeTab(tabIndex: number) {
		this.tabIndex = tabIndex;
	}

	addIcdValue(cgi) {
		this._icd.push(cgi);
		this.setPromptuaryValue(
			'icd10',
			this._icd.map((cgiObj) => cgiObj.id)
		);
	}

	removeIcdValue(cgiId) {
		this._icd = this._icd.filter((cgiValue) => cgiValue.id !== cgiId);
		this.setPromptuaryValue(
			'icd10',
			this._icd.map((cgiObj) => cgiObj.id)
		);
		if (this._icd.length === 0) {
			this.updateStep({ property: 'icd10', newValue: false });
		}
	}

	setPromptuaryValue(property: string, value: string | string[] | boolean) {
		const newValue = property === 'alarmSignals' ? true : Boolean(value);
		this.alarmSignals = newValue;
		const promptData: IPromptuary = {
			customerId: this.clientId,
			eventId: this.appointmentId,
			[property]: value,
		};

		this.promptuaryService.autoSaveField(promptData).subscribe(
			() => {
				this.unifiedPromptuary.autosaveComplete(property);
			},
			({ error }) => {
				this.notificationService.showError(error.messageI18n);
			}
		);

		if (this.formSteps[this.getStepByProperty(property)]) {
			this.updateStep({ newValue, property });
		}
	}

	getPromptuary() {
		this.promptuaryService
			.getData(this.appointment.id, this.appointment.profileId)
			.subscribe(
				(data) => {
					if (typeof data.alarmSignals === 'boolean') {
						this.alarmSignals = data.alarmSignals;
						this.setPromptuaryValue('alarmSignals', this.alarmSignals);
					}
					if (data.icd10?.length > 0) {
						this.updateStep({ property: 'icd10', newValue: true });
					}
				},
				() => {
					this.setPromptuaryValue('alarmSignals', false);
				}
			);
	}

	updateNoshow(noshowStatus: boolean): void {
		this.appointment.noShow = noshowStatus;
	}

	setAlarmsSignals(value: boolean): void {
		this.unifiedPromptuary.form.get('alarmSignals')?.setValue(value);
		if (!value) {
			this.unifiedPromptuary.form.get('alarmSignalsReason')?.reset();
		}
	}
}
